const DEFAULT_TOOLBAR_OPTIONS = {
  width: 280,
  height: 72,
  copyLabel: '📋 Copy',
  showLabel: '🔍 Show App',
};

const isFunction = (fn) => typeof fn === 'function';

export const isDocumentPictureInPictureSupported = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return Boolean(window.documentPictureInPicture);
};

/**
 * Open a lightweight always-on-top toolbar using the Document Picture-in-Picture API.
 * The returned window remains controllable by the caller, which allows re-use or updates.
 */
export async function openDictationToolbar(onCopy, onShowMain, options = {}) {
  if (!isFunction(onCopy) || !isFunction(onShowMain)) {
    throw new TypeError('openDictationToolbar requires onCopy and onShowMain callbacks');
  }

  if (!isDocumentPictureInPictureSupported()) {
    throw new Error('Document Picture-in-Picture is not supported in this browser.');
  }

  const { width, height, copyLabel, showLabel, onClose } = {
    ...DEFAULT_TOOLBAR_OPTIONS,
    ...options,
  };

  const controller = window.documentPictureInPicture;

  const pipWindow = await controller.requestWindow({ width, height });

  const doc = pipWindow.document;
  doc.open();
  doc.write('<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body></body></html>');
  doc.close();

  const style = doc.createElement('style');
  style.textContent = `
    :root {
      color-scheme: light dark;
      font: 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    body {
      margin: 0;
      padding: 10px;
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      background: rgba(18, 18, 20, 0.85);
      color: #fafafa;
    }
    button {
      all: unset;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 9999px;
      padding: 6px 16px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 150ms ease, transform 150ms ease;
      color: inherit;
    }
    button:hover {
      background: rgba(255, 255, 255, 0.25);
    }
    button:active {
      transform: scale(0.97);
    }
    button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  `;
  doc.head.appendChild(style);

  const copyBtn = doc.createElement('button');
  copyBtn.textContent = copyLabel;
  copyBtn.addEventListener('click', () => {
    try {
      const result = onCopy({ pipWindow });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => console.error('[Doc-PiP] copy handler failed', err));
      }
    } catch (err) {
      console.error('[Doc-PiP] copy handler failed', err);
    }
  });

  const showBtn = doc.createElement('button');
  showBtn.textContent = showLabel;
  showBtn.addEventListener('click', () => {
    try {
      const result = onShowMain({ pipWindow });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => console.error('[Doc-PiP] show handler failed', err));
      }
    } catch (err) {
      console.error('[Doc-PiP] show handler failed', err);
    }
  });

  doc.body.appendChild(copyBtn);
  doc.body.appendChild(showBtn);

  pipWindow.__toolbarElements = {
    copyBtn,
    showBtn,
  };

  const handleUnload = () => {
    if (typeof onClose === 'function') {
      try {
        onClose({ pipWindow });
      } catch (err) {
        console.error('[Doc-PiP] onClose handler failed', err);
      }
    }
    pipWindow.removeEventListener('pagehide', handleUnload);
    pipWindow.removeEventListener('unload', handleUnload);
  };

  pipWindow.addEventListener('pagehide', handleUnload);
  pipWindow.addEventListener('unload', handleUnload);

  return pipWindow;
}
